import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useOwner } from "../../../../context/OwnerContext";
import { useRole } from "../../../../context/RoleContext";
import { useAlert } from "../../../../context/AlertContext";
import ownerService from "../../../../services/owner.service";
import { IOwnerContract } from "../../../../services/api.types";

// Client-side sanity limit, mirroring the backend's multer `limits.fileSize`.
// UX ONLY — the server is the enforcement point for both type and size; a user
// can trivially bypass anything checked here. Never treat it as a control.
const MAX_CONTRACT_BYTES = 10 * 1024 * 1024;

const formatDate = (value: string | null): string =>
  value ? new Date(value).toLocaleString() : "";

const Contract = () => {
  const { t } = useTranslation();
  const { owner, refetch } = useOwner();
  // Affordance gate ONLY. The authoritative control is the backend's
  // restrictTo("super_admin") on POST /admins/owners/:id/contract — an RM that
  // forces this control open still receives a 403. Never describe this as the
  // security boundary.
  const { isSuperAdmin } = useRole();
  const { setAlert } = useAlert();

  const [contract, setContract] = useState<IOwnerContract | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const ownerId = owner?._id;

  const loadContract = useCallback(
    async (showSpinner: boolean) => {
      if (!ownerId) return;
      try {
        if (showSpinner) setLoading(true);
        // Each call mints a fresh short-lived presigned URL. It is held only in
        // component state for the current render and handed straight to
        // window.open — never persisted to storage, never logged (T-38-24).
        const data = await ownerService.getOwnerContract(ownerId);
        setContract(data);
      } catch (err: any) {
        setContract(null);
        // Covers the RM-IDOR case too: the backend 404s for an owner the RM
        // does not manage, so no metadata is ever rendered (T-38-25).
        setAlert("error", err?.message || t("ownerScreens.contractLoadError"));
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    // `setAlert` / `t` are stable enough here; depending on the owner *object*
    // would re-fetch every 20s because OwnerContext re-polls with a new identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ownerId]
  );

  useEffect(() => {
    loadContract(true);
  }, [loadContract]);

  const hasContract = Boolean(contract?.contractUploadedAt);

  const handleDownload = () => {
    if (!contract?.downloadUrl) return;
    window.open(contract.downloadUrl, "_blank", "noopener");
  };

  const handleFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    // Reset immediately so re-selecting the same file still fires `change`.
    event.target.value = "";
    if (!file || !ownerId) return;

    // UX-only pre-checks (see MAX_CONTRACT_BYTES) — the server re-validates both.
    if (file.type !== "application/pdf") {
      setAlert("error", t("ownerScreens.contractInvalidType"));
      return;
    }
    if (file.size > MAX_CONTRACT_BYTES) {
      setAlert("error", t("ownerScreens.contractTooLarge"));
      return;
    }

    try {
      setUploading(true);
      await ownerService.uploadOwnerContract(ownerId, file);
      setAlert("success", t("ownerScreens.contractUploadSuccess"));
      await loadContract(false);
      // Keep the owner document in context current too. The provider also
      // re-polls every 20s, so this is responsiveness, not correctness.
      await refetch();
    } catch (err: any) {
      setAlert("error", err?.message || t("ownerScreens.contractUploadError"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box className="p-4">
      <Paper className="p-6">
        <Typography variant="h5" gutterBottom>
          {t("ownerScreens.contract")}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t("ownerScreens.contractDesc")}
        </Typography>

        <Divider sx={{ my: 3 }} />

        {loading ? (
          <Box className="flex justify-center py-6">
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={2}>
            {hasContract ? (
              <Stack spacing={0.5}>
                <Typography variant="body2" color="text.secondary">
                  {t("ownerScreens.contractFileNameLabel")}:{" "}
                  <Typography component="span" variant="body2" color="text.primary">
                    {contract?.contractFileName}
                  </Typography>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("ownerScreens.contractUploadedOnLabel")}:{" "}
                  <Typography component="span" variant="body2" color="text.primary">
                    {formatDate(contract?.contractUploadedAt ?? null)}
                  </Typography>
                </Typography>
                {contract?.contractUploadedBy ? (
                  <Typography variant="body2" color="text.secondary">
                    {t("ownerScreens.contractUploadedByLabel")}:{" "}
                    <Typography component="span" variant="body2" color="text.primary">
                      {contract.contractUploadedBy}
                    </Typography>
                  </Typography>
                ) : null}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("ownerScreens.contractEmpty")}
              </Typography>
            )}

            <Stack direction="row" spacing={2}>
              {/* Download is available to BOTH super_admin and RM. */}
              {contract?.downloadUrl ? (
                <Button variant="outlined" onClick={handleDownload}>
                  {t("ownerScreens.contractDownloadLabel")}
                </Button>
              ) : null}

              {/* Upload/replace affordance — super_admin only (D-02). No delete
                  control exists on this screen by design (D-06). */}
              {isSuperAdmin ? (
                <Button
                  variant="contained"
                  component="label"
                  disabled={uploading}
                  startIcon={
                    uploading ? <CircularProgress size={16} color="inherit" /> : undefined
                  }
                >
                  {hasContract
                    ? t("ownerScreens.contractReplaceLabel")
                    : t("ownerScreens.contractUploadLabel")}
                  <input
                    ref={inputRef}
                    type="file"
                    hidden
                    accept="application/pdf,.pdf"
                    onChange={handleFileSelected}
                  />
                </Button>
              ) : null}
            </Stack>
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default Contract;
